/*
  Landing-page-only offer: a gift plus a discount code that only visitors of
  one page get (the "Landing page offer" block in featured-product).

  Why the theme does this and not a gift app: gift apps (BOGOS, DollarLabs)
  run on every page, so their offer also shows up on the PDP. Here the offer
  lives in the page's own section:

  1. After a successful add to cart of a qualifying variant from the page,
     the gift variant is added once, marked with `_gift_offer` /
     `_gift_offer_for` line properties (underscore = hidden at checkout).
  2. The discount code is then applied to the cart (Cart AJAX `discount`),
     which is what makes the gift free — the gift is added first so a
     "Buy X get Y" code already finds both X and Y in the cart. With
     "Apply the code" off, the customer enters it at checkout instead and
     the drawer shows the code on the gift line.
  3. The drawer is re-rendered so it shows the gift and the discount.

  The guard half runs wherever this file is loaded (the page itself, and
  every page whose cart already holds a gift line, see theme.liquid): when
  the drawer changes the cart and no qualifying line is left, the gift line
  is removed, so nobody checks out paying full price for a "free" gift.
*/
(() => {
  if (window.lpOffer) return;

  const GIFT_PROP = '_gift_offer';
  const FOR_PROP = '_gift_offer_for';
  const CODE_PROP = '_gift_offer_code';
  const ENTER_CODE_PROP = '_gift_offer_enter_code';
  const routes = window.routes || {};
  const cartUrl = routes.cart_url || '/cart';

  const request = (url, body) =>
    fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((response) => {
      if (!response.ok) throw new Error(`${url} → ${response.status}`);
      return response.json();
    });

  const getCart = () => request(`${cartUrl}.js`);
  const giftProps = (item) => (item && item.properties) || {};
  const isGiftLine = (item) => Boolean(giftProps(item)[GIFT_PROP]);

  function readOffers() {
    return [...document.querySelectorAll('[data-lp-offer-config]')]
      .map((el) => {
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return null;
        }
      })
      .filter((offer) => offer && (offer.code || offer.giftVariantId));
  }

  const qualifies = (offer, variantId) =>
    offer.variantIds.length === 0 || offer.variantIds.includes(String(variantId));

  // Re-renders the drawer (items, footer, icon bubble) from the current cart.
  async function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lp-offer' });
      return;
    }
    const ids = drawer.getSectionsToRender().map((section) => section.id);
    // No JSON Accept header here: with it, /cart answers with the cart
    // itself instead of the rendered sections.
    const response = await fetch(`${cartUrl}?sections=${ids.join(',')}`);
    if (!response.ok) throw new Error(`sections → ${response.status}`);
    const sections = await response.json();
    if (!ids.every((id) => typeof sections[id] === 'string')) throw new Error('sections missing');
    // No { open: true }: this is a background repaint, not an add to cart.
    drawer.renderContents({ sections });
  }

  let busy = false;

  async function applyOffer(variantId) {
    const offer = readOffers().find((candidate) => qualifies(candidate, variantId));
    if (!offer || busy) return;
    busy = true;
    try {
      let cart = await getCart();
      let changed = false;

      const hasGift = cart.items.some((item) => giftProps(item)[GIFT_PROP] === offer.id);
      if (offer.addGift && offer.giftVariantId && !hasGift) {
        const requiredIds = offer.variantIds.length ? offer.variantIds : [String(variantId)];
        const properties = { [GIFT_PROP]: offer.id, [FOR_PROP]: requiredIds.join(',') };
        // Applied by the page: the gift may only stay while that code works.
        // Entered by the customer at checkout: the drawer tells them which code.
        if (offer.code && offer.applyCode !== false) properties[CODE_PROP] = offer.code;
        if (offer.code && offer.applyCode === false) properties[ENTER_CODE_PROP] = offer.code;
        await request(`${routes.cart_add_url || '/cart/add'}.js`, {
          items: [{ id: offer.giftVariantId, quantity: offer.giftQuantity || 1, properties }],
        });
        changed = true;
      }

      if (offer.code && offer.applyCode !== false && codeState(cart, offer.code) === 'missing') {
        // `discount` replaces the cart's codes, so keep the ones already there.
        const codes = (cart.discount_codes || []).map((discount) => discount.code);
        await request(`${routes.cart_update_url || '/cart/update'}.js`, {
          discount: codes.concat(offer.code).join(','),
        });
        changed = true;
      }

      // Whichever way the cart got here (code applied just now, or already
      // on the cart from an earlier visit), the gift only stays while its
      // code actually applies. Otherwise it would sit there at full price.
      if (changed) cart = await getCart();
      if (await removeGifts(cart)) changed = true;

      if (changed) await refreshDrawer();
    } catch (error) {
      console.error('[lp-offer]', error);
    } finally {
      busy = false;
    }
  }


  // 'ok' | 'invalid' (on the cart but Shopify won't apply it) | 'missing'
  function codeState(cart, code) {
    const found = (cart.discount_codes || []).find(
      (discount) => discount.code.toLowerCase() === String(code).toLowerCase()
    );
    if (!found) return 'missing';
    return found.applicable === false ? 'invalid' : 'ok';
  }

  // Gift lines that must go: their qualifying product left the cart, or the
  // code that makes them free is missing or no longer applies.
  function giftsToRemove(cart) {
    const gifts = cart.items.filter(isGiftLine);
    const others = cart.items.filter((item) => !isGiftLine(item));
    return gifts.filter((gift) => {
      const props = giftProps(gift);
      const required = String(props[FOR_PROP] || '')
        .split(',')
        .filter(Boolean);
      const orphaned = required.length > 0 && !others.some((item) => required.includes(String(item.variant_id)));
      const unpaid = Boolean(props[CODE_PROP]) && codeState(cart, props[CODE_PROP]) !== 'ok';
      return orphaned || unpaid;
    });
  }

  // Removes those lines (and an offer code that no longer applies). Returns
  // whether it changed the cart; the caller refreshes the drawer.
  async function removeGifts(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    const remove = giftsToRemove(cart);
    if (!remove.length) return false;
    const body = { updates: {} };
    remove.forEach((gift) => {
      body.updates[gift.key] = 0;
    });
    const deadCodes = remove
      .map((gift) => giftProps(gift)[CODE_PROP])
      .filter((code) => code && codeState(cart, code) === 'invalid')
      .map((code) => code.toLowerCase());
    if (deadCodes.length) {
      body.discount = (cart.discount_codes || [])
        .map((discount) => discount.code)
        .filter((code) => !deadCodes.includes(code.toLowerCase()))
        .join(',');
    }
    console.warn('[lp-offer] free gift removed: its product or code no longer qualifies');
    await request(`${routes.cart_update_url || '/cart/update'}.js`, body);
    return true;
  }

  async function removeOrphanGifts(cart) {
    if (busy) {
      scheduleVerify();
      return;
    }
    let removed = false;
    try {
      removed = await removeGifts(cart);
      if (removed) await refreshDrawer();
    } catch (error) {
      console.error('[lp-offer]', error);
    }
    if (!removed) {
      verifyAttempts = 0;
      return;
    }
    // Gift apps (BOGOS) rewrite the cart at the same moment, which can undo
    // the removal: look again once they have settled.
    if (verifyAttempts < MAX_VERIFY_ATTEMPTS) {
      verifyAttempts += 1;
      scheduleVerify();
    }
  }

  // Re-reads the cart a moment after a change and removes orphaned gifts.
  // Covers removals the event above misses or that another script undoes.
  const MAX_VERIFY_ATTEMPTS = 3;
  let verifyAttempts = 0;
  let verifyTimer = null;
  function scheduleVerify(delay = 1500) {
    clearTimeout(verifyTimer);
    verifyTimer = setTimeout(async () => {
      if (busy) return scheduleVerify(delay);
      try {
        await removeOrphanGifts(await getCart());
      } catch (error) {
        console.error('[lp-offer]', error);
      }
    }, delay);
  }

  // Shows the gift card only while a qualifying variant is selected.
  function initCards() {
    document.querySelectorAll('[data-lp-offer]').forEach((root) => {
      if (root.dataset.lpOfferInit) return;
      root.dataset.lpOfferInit = '1';
      const config = root.querySelector('[data-lp-offer-config]');
      const card = root.querySelector('.lpo__item');
      if (!config || !card) return;
      const offer = JSON.parse(config.textContent);
      const scope = root.closest('featured-product-form') || document;
      const update = (variantId) => {
        const on = qualifies(offer, variantId);
        card.classList.toggle('is-active', on);
        card.setAttribute('aria-hidden', on ? 'false' : 'true');
      };
      scope.querySelectorAll('[data-featured-product-variant-picker]').forEach((picker) => {
        picker.addEventListener('change', (event) => update(event.target.value));
      });
      const checked = scope.querySelector('[data-featured-product-variant-picker]:checked');
      update(checked ? checked.value : '');
    });
  }

  function init() {
    initCards();
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event) return;
      if (event.source === 'product-form' && event.productVariantId) {
        applyOffer(event.productVariantId);
      } else if (event.source === 'cart-items') {
        removeOrphanGifts(event.cartData);
        scheduleVerify();
      } else if (event.source !== 'lp-offer') {
        scheduleVerify();
      }
    });
    // A gift can be left over from an earlier visit (e.g. the qualifying
    // product was removed on another device or tab): check once on load.
    scheduleVerify(0);
  }

  window.lpOffer = { applyOffer, removeOrphanGifts };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
